import * as THREE from "three";
import { RigidBody } from "@react-three/rapier";
import type { Vec3 } from "@solblox/shared";

import { Prop } from "./Prop";
import { PBRSurfaceMaterial } from "./PBRSurface";
import { ProceduralStoneFloor } from "./ProceduralStoneFloor";
import { ProceduralStoneWallMaterial } from "./ProceduralStoneWallMaterial";
import { ThroneVideoScreen } from "./ThroneVideoScreen";
import { useLaunchTheme } from "../launch/LaunchProvider";

/**
 * Screen sources come from the active launch theme (see
 * `client/public/launches/<id>/theme.json`). The hardcoded defaults
 * inside MAIN_THEME mirror the legacy `/videos/1.webm` + `/videos/2.webm`
 * paths so this scene renders identically when no `?launch=` is set.
 * Back screen sits behind the throne; entrance screen hangs above the
 * castle door facing back into the hall (arena-jumbotron layout).
 */

/**
 * Cathedral-scale medieval throne hall. Throne anchor at the world origin
 * [0,0,0]; throne faces +z (toward the entrance). All walls, floor, columns
 * and the ribbed-vault ceiling are primitive geometry tuned to read as
 * warm sandstone. Decoration props are real Poly Haven CC0 GLTFs.
 */

export const HALL_HALF_WIDTH = 14;
export const HALL_HEIGHT = 13;
export const HALL_BACK_Z = -8;
export const HALL_FRONT_Z = 26;
const HALL_DEPTH = HALL_FRONT_Z - HALL_BACK_Z;
const HALL_CENTER_Z = (HALL_FRONT_Z + HALL_BACK_Z) / 2;
const WALL_THICK = 0.4;

const WALL_COLOR = "#8a7a64";
const CEILING_COLOR = "#5a4838";
const RIB_COLOR = "#a89274";
const BOSS_COLOR = "#c9b08a";
const BANNER_RED = "#7a1a1a";
const BANNER_BLUE = "#1a2a6a";
const BANNER_GOLD = "#a07a18";

export const HALL_SPAWN: Vec3 = { x: 0, y: 1, z: HALL_FRONT_Z - 4 };

interface ThroneHallProps {
  withPhysics?: boolean;
}

export function ThroneHall({ withPhysics = false }: ThroneHallProps): JSX.Element {
  const theme = useLaunchTheme();
  const throneScreenSrc = theme.screens.throne;
  const entranceScreenSrc = theme.screens.entrance;
  return (
    <group>
      {/* Floor — procedural flagstone with beveled grout, per-tile color
          variation, and dirt noise. No external texture files. */}
      <ProceduralStoneFloor
        width={HALL_HALF_WIDTH * 2}
        depth={HALL_DEPTH}
        position={[0, 0.01, HALL_CENTER_Z]}
        repeat={[7, 8.5]}
      />

      {/* Ribbed-vault ceiling — replaces the flat plane */}
      <VaultedCeiling />

      {/* Walls */}
      <Wall
        position={[0, HALL_HEIGHT / 2, HALL_BACK_Z]}
        size={[HALL_HALF_WIDTH * 2, HALL_HEIGHT, WALL_THICK]}
        withPhysics={withPhysics}
      />
      <Wall
        position={[0, HALL_HEIGHT / 2, HALL_FRONT_Z]}
        size={[HALL_HALF_WIDTH * 2, HALL_HEIGHT, WALL_THICK]}
        withPhysics={withPhysics}
      />
      <Wall
        position={[-HALL_HALF_WIDTH, HALL_HEIGHT / 2, HALL_CENTER_Z]}
        size={[WALL_THICK, HALL_HEIGHT, HALL_DEPTH]}
        withPhysics={withPhysics}
      />
      <Wall
        position={[HALL_HALF_WIDTH, HALL_HEIGHT / 2, HALL_CENTER_Z]}
        size={[WALL_THICK, HALL_HEIGHT, HALL_DEPTH]}
        withPhysics={withPhysics}
      />

      {/* Stepped dais — three rectangular tiers leading up to the throne.
          Each tier is collidable so the avatar climbs the steps instead of
          sliding through them into the throne base. */}
      <DaisStep y={0.0} halfW={3.4} halfD={2.6} h={0.25} withPhysics={withPhysics} />
      <DaisStep y={0.25} halfW={2.8} halfD={2.1} h={0.25} withPhysics={withPhysics} />
      <DaisStep y={0.50} halfW={2.2} halfD={1.6} h={0.30} withPhysics={withPhysics} />

      {/* Red carpet runner — throne base to entrance */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.02, (HALL_FRONT_Z + 2) / 2]}
        receiveShadow
      >
        <planeGeometry args={[2.8, HALL_FRONT_Z - 2]} />
        <meshStandardMaterial color="#7a1818" roughness={0.85} />
      </mesh>
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[-1.35, 0.025, (HALL_FRONT_Z + 2) / 2]}
      >
        <planeGeometry args={[0.12, HALL_FRONT_Z - 2]} />
        <meshStandardMaterial color="#c8a04a" roughness={0.6} />
      </mesh>
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[1.35, 0.025, (HALL_FRONT_Z + 2) / 2]}
      >
        <planeGeometry args={[0.12, HALL_FRONT_Z - 2]} />
        <meshStandardMaterial color="#c8a04a" roughness={0.6} />
      </mesh>

      {/* Stone columns — perimeter, between window bays */}
      {[-6, 0, 6].map((z) => (
        <Column key={`L${z}`} position={[-HALL_HALF_WIDTH + 0.7, 0, z]} />
      ))}
      {[-6, 0, 6].map((z) => (
        <Column key={`R${z}`} position={[HALL_HALF_WIDTH - 0.7, 0, z]} />
      ))}
      {[12, 18].map((z) => (
        <Column key={`L2${z}`} position={[-HALL_HALF_WIDTH + 0.7, 0, z]} />
      ))}
      {[12, 18].map((z) => (
        <Column key={`R2${z}`} position={[HALL_HALF_WIDTH - 0.7, 0, z]} />
      ))}

      {/* Gothic arched windows — four bays per side wall */}
      {[-4, 2, 9, 17].map((z) => (
        <GothicWindow key={`WL${z}`} side="left" z={z} />
      ))}
      {[-4, 2, 9, 17].map((z) => (
        <GothicWindow key={`WR${z}`} side="right" z={z} />
      ))}

      {/* Giant media screen on the back wall behind the throne. Sits flush
          against the wall with the KingNameplate floating in front of it.
          Plays the entrance clip (videos swapped vs the theme defaults). */}
      <ThroneVideoScreen
        src={entranceScreenSrc}
        position={[0, 5.0, HALL_BACK_Z + WALL_THICK / 2 + 0.02]}
        width={7}
        height={5}
      />

      {/* Second screen above the castle door, facing into the hall — gives
          the player a second jumbotron to look at when they turn around at
          the throne. Mounted at y=10 to clear the door (scale 1.5, ~7.5m tall)
          and offset 0.05m off the inside wall face to avoid z-fighting.
          Plays the throne clip (videos swapped vs the theme defaults). */}
      <ThroneVideoScreen
        src={throneScreenSrc}
        position={[0, 10.0, HALL_FRONT_Z - WALL_THICK / 2 - 0.05]}
        rotation={[0, Math.PI, 0]}
        width={6}
        height={4}
      />

      {/* Banners — back wall flanking the KingNameplate */}
      <Banner
        position={[-4.5, 6.0, HALL_BACK_Z + WALL_THICK / 2 + 0.05]}
        color={BANNER_RED}
      />
      <Banner
        position={[4.5, 6.0, HALL_BACK_Z + WALL_THICK / 2 + 0.05]}
        color={BANNER_BLUE}
      />
      {/* Banners — side walls between windows */}
      <Banner
        position={[-HALL_HALF_WIDTH + WALL_THICK / 2 + 0.05, 6.0, -1]}
        rotation={[0, Math.PI / 2, 0]}
        color={BANNER_GOLD}
      />
      <Banner
        position={[HALL_HALF_WIDTH - WALL_THICK / 2 - 0.05, 6.0, -1]}
        rotation={[0, -Math.PI / 2, 0]}
        color={BANNER_GOLD}
      />

      {/* Torches with point lights */}
      <Torch position={[-HALL_HALF_WIDTH + 1.5, 4.5, HALL_BACK_Z + 0.4]} />
      <Torch position={[HALL_HALF_WIDTH - 1.5, 4.5, HALL_BACK_Z + 0.4]} />
      <Torch
        position={[-HALL_HALF_WIDTH + 0.5, 4.5, 5]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <Torch
        position={[HALL_HALF_WIDTH - 0.5, 4.5, 5]}
        rotation={[0, -Math.PI / 2, 0]}
      />
      <Torch
        position={[-HALL_HALF_WIDTH + 0.5, 4.5, 12]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <Torch
        position={[HALL_HALF_WIDTH - 0.5, 4.5, 12]}
        rotation={[0, -Math.PI / 2, 0]}
      />
      <Torch position={[-HALL_HALF_WIDTH + 1.5, 4.5, HALL_FRONT_Z - 0.4]} />
      <Torch position={[HALL_HALF_WIDTH - 1.5, 4.5, HALL_FRONT_Z - 0.4]} />

      {/* === GLTF props === */}
      {/* Castle door at the entrance */}
      <Prop
        url="/models/large_castle_door/large_castle_door_1k.gltf"
        position={[0, 0, HALL_FRONT_Z - 0.5]}
        rotation={[0, Math.PI, 0]}
        scale={1.5}
      />
      {/* Centered chandelier hanging from a vault boss directly over the throne */}
      <Prop
        url="/models/Chandelier_01/Chandelier_01_1k.gltf"
        position={[0, HALL_HEIGHT - 0.6, 0]}
      />
      {/* Symmetric floor light pair flanking the dais */}
      <Prop
        url="/models/Lantern_01/Lantern_01_1k.gltf"
        position={[-3.8, 0, 1.5]}
      />
      <Prop
        url="/models/brass_candleholders/brass_candleholders_1k.gltf"
        position={[3.8, 0, 1.5]}
      />

      {/* Side tables flanking the throne — console tables turned to face the
          central aisle, dressed with brass props that match the gold trim. */}
      <Prop
        url="/models/chinese_console_table/chinese_console_table_1k.gltf"
        position={[-3.6, 0, -0.4]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <Prop
        url="/models/brass_goblets/brass_goblets_1k.gltf"
        position={[-3.6, 0.78, -0.4]}
      />
      <Prop
        url="/models/chinese_console_table/chinese_console_table_1k.gltf"
        position={[3.6, 0, -0.4]}
        rotation={[0, -Math.PI / 2, 0]}
      />
      <Prop
        url="/models/brass_vase_03/brass_vase_03_1k.gltf"
        position={[3.6, 0.78, -0.4]}
      />

      {/* === Furniture — feast/council hall dressing ===
          Placed in the empty bays between side-wall columns at z=0/6 and
          z=12/18, against the side walls so the central carpet stays clear.
          Furniture faces the central aisle (left wall: RotY=+π/2 → +x;
          right wall: RotY=-π/2 → -x). Avoids columns, lanterns, plaques. */}

      {/* Bay at z≈4 — console table + armchair pair, both sides */}
      <Prop
        url="/models/chinese_console_table/chinese_console_table_1k.gltf"
        position={[-12.4, 0, 4]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <Prop
        url="/models/chinese_armchair/chinese_armchair_1k.gltf"
        position={[-11.4, 0, 5.5]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <Prop
        url="/models/chess_set/chess_set_1k.gltf"
        position={[-12.4, 0.78, 4]}
      />

      <Prop
        url="/models/chinese_console_table/chinese_console_table_1k.gltf"
        position={[12.4, 0, 4]}
        rotation={[0, -Math.PI / 2, 0]}
      />
      <Prop
        url="/models/chinese_armchair/chinese_armchair_1k.gltf"
        position={[11.4, 0, 5.5]}
        rotation={[0, -Math.PI / 2, 0]}
      />
      <Prop
        url="/models/brass_goblets/brass_goblets_1k.gltf"
        position={[12.4, 0.78, 4]}
      />

      {/* Bay at z≈15 — sofa + stool, both sides (lounge area near entrance) */}
      <Prop
        url="/models/chinese_sofa/chinese_sofa_1k.gltf"
        position={[-12.2, 0, 15]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <Prop
        url="/models/chinese_stool/chinese_stool_1k.gltf"
        position={[-10.4, 0, 14.2]}
      />
      <Prop
        url="/models/chinese_stool/chinese_stool_1k.gltf"
        position={[-10.4, 0, 15.8]}
      />

      <Prop
        url="/models/chinese_sofa/chinese_sofa_1k.gltf"
        position={[12.2, 0, 15]}
        rotation={[0, -Math.PI / 2, 0]}
      />
      <Prop
        url="/models/chinese_console_table/chinese_console_table_1k.gltf"
        position={[10.4, 0, 15]}
        rotation={[0, Math.PI, 0]}
      />
      <Prop
        url="/models/book_encyclopedia_set_01/book_encyclopedia_set_01_1k.gltf"
        position={[10.4, 0.78, 15]}
      />

      {/* Bay at z≈21 — second pairing closer to the entrance to fill the
          long approach. Stays clear of the door at z=25.5 and columns at z=18. */}
      <Prop
        url="/models/chinese_armchair/chinese_armchair_1k.gltf"
        position={[-11.4, 0, 21]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <Prop
        url="/models/chinese_armchair/chinese_armchair_1k.gltf"
        position={[11.4, 0, 21]}
        rotation={[0, -Math.PI / 2, 0]}
      />
      <Prop
        url="/models/brass_vase_02/brass_vase_02_1k.gltf"
        position={[-13.2, 0, 22.5]}
      />
      <Prop
        url="/models/brass_vase_02/brass_vase_02_1k.gltf"
        position={[13.2, 0, 22.5]}
      />

      {/* Behind the throne — cabinet centered, folding screens flanking.
          Back wall is at z=-8; throne back panel is at z=-0.65. */}
      <Prop
        url="/models/GothicCabinet_01/GothicCabinet_01_1k.gltf"
        position={[0, 0, -7.4]}
      />
      <Prop
        url="/models/chinese_screen_panels/chinese_screen_panels_1k.gltf"
        position={[-5.5, 0, -7.0]}
        rotation={[0, Math.PI / 6, 0]}
      />
      <Prop
        url="/models/chinese_screen_panels/chinese_screen_panels_1k.gltf"
        position={[5.5, 0, -7.0]}
        rotation={[0, -Math.PI / 6, 0]}
      />
    </group>
  );
}

interface WallProps {
  position: [number, number, number];
  size: [number, number, number];
  withPhysics: boolean;
}
function Wall({ position, size, withPhysics }: WallProps): JSX.Element {
  // Repeat tiling scales with wall size so each canvas tile (4 brick cols
  // × 6 brick rows) maps to ~4m × ~4m of wall. Divisors picked so bricks
  // read ~1m wide × ~0.6m tall — medieval throne-hall scale.
  const repeatU = Math.max(size[0], size[2]) / 4;
  const repeatV = size[1] / 3;
  const mesh = (
    <mesh receiveShadow castShadow>
      <boxGeometry args={size} />
      <ProceduralStoneWallMaterial
        repeat={[repeatU, repeatV]}
        color={WALL_COLOR}
      />
    </mesh>
  );
  if (withPhysics) {
    return (
      <RigidBody type="fixed" colliders="cuboid" position={position}>
        {mesh}
      </RigidBody>
    );
  }
  return <group position={position}>{mesh}</group>;
}

interface BannerProps {
  position: [number, number, number];
  rotation?: [number, number, number];
  color: string;
}
function Banner({ position, rotation = [0, 0, 0], color }: BannerProps): JSX.Element {
  return (
    <group position={position} rotation={rotation}>
      <mesh>
        <planeGeometry args={[1.6, 3.5]} />
        <meshStandardMaterial color={color} side={THREE.DoubleSide} roughness={0.8} />
      </mesh>
      <mesh position={[0, 1.7, 0.005]}>
        <planeGeometry args={[1.7, 0.16]} />
        <meshStandardMaterial color="#c8a04a" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

interface DaisStepProps {
  y: number;
  halfW: number;
  halfD: number;
  h: number;
  withPhysics?: boolean;
}
function DaisStep({
  y,
  halfW,
  halfD,
  h,
  withPhysics = false,
}: DaisStepProps): JSX.Element {
  const mesh = (
    <mesh castShadow receiveShadow>
      <boxGeometry args={[halfW * 2, h, halfD * 2]} />
      <meshStandardMaterial color="#3a3328" roughness={0.85} />
    </mesh>
  );
  if (withPhysics) {
    return (
      <RigidBody type="fixed" colliders="cuboid" position={[0, y + h / 2, 0]}>
        {mesh}
      </RigidBody>
    );
  }
  return <group position={[0, y + h / 2, 0]}>{mesh}</group>;
}

interface ColumnProps {
  position: [number, number, number];
}
function Column({ position }: ColumnProps): JSX.Element {
  return (
    <group position={position}>
      <mesh position={[0, HALL_HEIGHT / 2, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.4, 0.45, HALL_HEIGHT, 16]} />
        <meshStandardMaterial color="#7a6a55" roughness={0.95} />
      </mesh>
      <mesh position={[0, 0.18, 0]} castShadow>
        <boxGeometry args={[1.2, 0.36, 1.2]} />
        <meshStandardMaterial color="#5a4d3e" roughness={0.95} />
      </mesh>
      <mesh position={[0, HALL_HEIGHT - 0.18, 0]} castShadow>
        <boxGeometry args={[1.2, 0.36, 1.2]} />
        <meshStandardMaterial color="#5a4d3e" roughness={0.95} />
      </mesh>
    </group>
  );
}

interface GothicWindowProps {
  side: "left" | "right";
  z: number;
}
function GothicWindow({ side, z }: GothicWindowProps): JSX.Element {
  const sign = side === "left" ? -1 : 1;
  const x = sign * (HALL_HALF_WIDTH - WALL_THICK / 2 - 0.02);
  const rotY = side === "left" ? Math.PI / 2 : -Math.PI / 2;
  const winW = 1.8;
  const winH = 5.0;
  const baseY = 1.5;
  return (
    <group position={[x, baseY + winH / 2, z]} rotation={[0, rotY, 0]}>
      <mesh>
        <planeGeometry args={[winW, winH]} />
        <meshStandardMaterial
          color="#ffd486"
          emissive="#ffb86b"
          emissiveIntensity={1.8}
          roughness={0.4}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh position={[0, winH / 2, 0]}>
        <circleGeometry args={[winW / 2, 20, 0, Math.PI]} />
        <meshStandardMaterial
          color="#ffd486"
          emissive="#ffb86b"
          emissiveIntensity={1.8}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh position={[-winW / 4, 0, 0.01]}>
        <planeGeometry args={[0.08, winH]} />
        <meshStandardMaterial color="#2a2418" side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[winW / 4, 0, 0.01]}>
        <planeGeometry args={[0.08, winH]} />
        <meshStandardMaterial color="#2a2418" side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 0, 0.01]}>
        <planeGeometry args={[0.08, winH]} />
        <meshStandardMaterial color="#2a2418" side={THREE.DoubleSide} />
      </mesh>
      <pointLight color="#ffb86b" intensity={1.2} distance={12} decay={1.6} />
    </group>
  );
}

interface TorchProps {
  position: [number, number, number];
  rotation?: [number, number, number];
}
function Torch({ position, rotation = [0, 0, 0] }: TorchProps): JSX.Element {
  return (
    <group position={position} rotation={rotation}>
      <mesh castShadow>
        <cylinderGeometry args={[0.07, 0.07, 0.6, 8]} />
        <meshStandardMaterial color="#3a2a18" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.4, 0]}>
        <sphereGeometry args={[0.14, 8, 8]} />
        <meshStandardMaterial
          color="#ffb060"
          emissive="#ffb060"
          emissiveIntensity={2.5}
        />
      </mesh>
      <pointLight
        position={[0, 0.45, 0]}
        color="#ffb060"
        intensity={1.6}
        distance={11}
        decay={1.6}
      />
    </group>
  );
}

/**
 * Ribbed-vault ceiling — grid of bays, each with an X-pattern of stone
 * ribs converging on a central boss. Reads as gothic cross-rib vaulting at
 * a distance. Flat backplane behind the ribs to give the upper surface a
 * solid stone color.
 */
function VaultedCeiling(): JSX.Element {
  const baysX = 4;
  const baysZ = 5;
  const bayW = (HALL_HALF_WIDTH * 2) / baysX;
  const bayD = HALL_DEPTH / baysZ;
  const bays: JSX.Element[] = [];
  for (let i = 0; i < baysX; i++) {
    for (let j = 0; j < baysZ; j++) {
      const cx = -HALL_HALF_WIDTH + bayW * (i + 0.5);
      const cz = HALL_BACK_Z + bayD * (j + 0.5);
      bays.push(
        <VaultBay key={`${i}-${j}`} cx={cx} cz={cz} bayW={bayW} bayD={bayD} />,
      );
    }
  }
  return (
    <group>
      {/* Backplane behind the rib pattern */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, HALL_HEIGHT, HALL_CENTER_Z]}>
        <planeGeometry args={[HALL_HALF_WIDTH * 2, HALL_DEPTH]} />
        <PBRSurfaceMaterial
          name="ceiling-stone"
          color={CEILING_COLOR}
          roughness={1.0}
          repeat={[6, 8]}
        />
      </mesh>
      {/* Transverse ribs along bay edges — depth direction */}
      {Array.from({ length: baysZ + 1 }, (_, j) => {
        const z = HALL_BACK_Z + bayD * j;
        return (
          <mesh key={`tz${j}`} position={[0, HALL_HEIGHT - 0.1, z]} castShadow>
            <boxGeometry args={[HALL_HALF_WIDTH * 2, 0.2, 0.28]} />
            <meshStandardMaterial color={RIB_COLOR} roughness={0.9} />
          </mesh>
        );
      })}
      {/* Transverse ribs — width direction */}
      {Array.from({ length: baysX + 1 }, (_, i) => {
        const x = -HALL_HALF_WIDTH + bayW * i;
        return (
          <mesh key={`tx${i}`} position={[x, HALL_HEIGHT - 0.1, HALL_CENTER_Z]} castShadow>
            <boxGeometry args={[0.28, 0.2, HALL_DEPTH]} />
            <meshStandardMaterial color={RIB_COLOR} roughness={0.9} />
          </mesh>
        );
      })}
      {/* Per-bay diagonal X ribs + central boss */}
      {bays}
    </group>
  );
}

interface VaultBayProps {
  cx: number;
  cz: number;
  bayW: number;
  bayD: number;
}
function VaultBay({ cx, cz, bayW, bayD }: VaultBayProps): JSX.Element {
  const diag = Math.sqrt(bayW * bayW + bayD * bayD);
  const angle = Math.atan2(bayD, bayW);
  return (
    <group position={[cx, HALL_HEIGHT - 0.05, cz]}>
      <mesh rotation={[0, angle, 0]}>
        <boxGeometry args={[diag, 0.18, 0.22]} />
        <meshStandardMaterial color={RIB_COLOR} roughness={0.9} />
      </mesh>
      <mesh rotation={[0, -angle, 0]}>
        <boxGeometry args={[diag, 0.18, 0.22]} />
        <meshStandardMaterial color={RIB_COLOR} roughness={0.9} />
      </mesh>
      {/* central boss */}
      <mesh position={[0, -0.05, 0]} castShadow>
        <sphereGeometry args={[0.32, 16, 16]} />
        <meshStandardMaterial color={BOSS_COLOR} roughness={0.85} metalness={0.05} />
      </mesh>
    </group>
  );
}
